import { useState } from 'react';
import { ApolloProvider, useQuery, useApolloClient } from '@apollo/client';
import withTranslation from '@helpers/i18n';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { IconClipboardList, IconMessage, IconXmark } from '@dokeos/icons';
import { Badge } from '@dokeos/syllabus/badge';
import { Button } from '@dokeos/syllabus/button';
import { IconButton } from '@dokeos/syllabus/icon-button';
import { Text } from '@dokeos/syllabus/text';
import { Toolbar, ToolbarButton, ToolbarSeparator } from '@dokeos/syllabus/toolbar';
import getApiClient, { SESSION_KEY } from 'react/services/api_client';
import PendingSatisfactionSurveysQuery from './pending_satisfaction_surveys.graphql';
import SatisfactionMeter from './index';

const apiClient = getApiClient();

const rise = keyframes`
  from {
    opacity: 0;
    translate: 0 var(--spacing-md);
  }
`;

/**
 * A floating design-system Toolbar at the bottom of the window rather than a band over the
 * page: beside the app shell's rail an absolutely placed band sat on the page title. The
 * icon and the short rise mark it as a request, not as page chrome. Wraps rather than
 * overflowing a narrow window, and stays inside it.
 */
const FloatingBar = styled(Toolbar)`
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--spacing-md);
  width: max-content;
  max-width: calc(100vw - 2 * var(--spacing-md));
  padding: var(--spacing-md);
  animation: ${rise} 300ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const IconDisc = styled.span`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: calc(var(--spacing) * 10);
  height: calc(var(--spacing) * 10);
  border-radius: 50%;
  background: color-mix(in oklab, var(--primary, var(--color-primary)) 10%, var(--color-surface));
  color: var(--primary, var(--color-primary));

  & > svg {
    width: var(--icon-size-md);
    height: var(--icon-size-md);
  }
`;

/**
 * The brand Button in the portal's own colour (`--primary` / `--primary-hover`, set by
 * `classes/color_application.js`, as the legacy buttons use), falling back to the design
 * system's brand colour. `&&&` outranks app_shell_host.css' `[data-syllabus]` button reset.
 */
const SurveyButton = styled(Button)`
  &&& {
    background-color: var(--primary, var(--color-button-brand-bg));
    color: var(--color-button-brand-text);
  }

  &&&:hover {
    background-color: var(--primary-hover, var(--color-button-brand-bg-hover));
  }
`;

const Copy = styled.span`
  display: flex;
  flex-direction: column;
`;

const VISIBLE_LIMIT = 5;

const truncateName = (name, max = 25) => (name.length > max ? `${name.slice(0, max)}…` : name);

const SatisfactionSurveyBanner = ({ t }) => {
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [completedIds, setCompletedIds] = useState([]);
  const client = useApolloClient();

  const { data } = useQuery(PendingSatisfactionSurveysQuery, {
    fetchPolicy: 'cache-first',
  });

  const surveys = (data?.pending_satisfaction_surveys || []).filter(
    (s) => !completedIds.includes(s.id),
  );

  const handleSurveySubmitted = () => {
    const submittedId = selectedSurvey?.id;
    setCompletedIds((prev) => [...prev, submittedId]);
    client.cache.modify({
      fields: {
        pending_satisfaction_surveys(existingRefs = [], { readField }) {
          return existingRefs.filter((ref) => readField('id', ref) !== submittedId);
        },
      },
    });
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(client.cache.extract()));
    } catch {
      /* ignore */
    }
    setSelectedSurvey(null);
  };

  if (surveys.length === 0 || dismissed) return null;

  const visibleSurveys = surveys.slice(0, VISIBLE_LIMIT);
  const hiddenCount = surveys.length - VISIBLE_LIMIT;

  return (
    <>
      {/* Portalled to <body>: the app shell's page column is a CSS container, which would
          make it, not the window, what a fixed element is placed against. */}
      {createPortal(
        <FloatingBar
          position="floating"
          aria-label={t('.your_feedback_helps')}
          data-syllabus=""
          data-purpose="satisfaction_survey_banner"
        >
          <IconDisc aria-hidden="true">
            <IconMessage />
          </IconDisc>
          <Copy>
            <Text as="span" size="md" weight="bold">
              {t('.headline')}
            </Text>
            <Text as="span" size="sm" color="muted">
              {t('.your_feedback_helps')}
            </Text>
          </Copy>
          {visibleSurveys.map((survey) => (
            <ToolbarButton key={survey.id} asChild>
              <SurveyButton
                size="md"
                color="brand"
                variant="primary"
                prefixIcon={<IconClipboardList />}
                data-purpose="satisfaction_survey_banner_training_link"
                onClick={() => setSelectedSurvey(survey)}
              >
                <span title={survey.training_name}>{truncateName(survey.training_name)}</span>
              </SurveyButton>
            </ToolbarButton>
          ))}
          {hiddenCount > 0 && (
            <Badge
              color="neutral"
              variant="secondary"
              size="md"
              data-purpose="satisfaction_survey_banner_overflow_badge"
            >
              +{hiddenCount}
            </Badge>
          )}
          <ToolbarSeparator />
          <ToolbarButton asChild>
            <IconButton
              label={t('.close')}
              icon={<IconXmark />}
              color="neutral"
              variant="link"
              size="sm"
              data-purpose="satisfaction_survey_banner_close"
              onClick={() => setDismissed(true)}
            />
          </ToolbarButton>
        </FloatingBar>,
        document.body,
      )}

      <SatisfactionMeter
        key={selectedSurvey?.id}
        participationId={selectedSurvey?.id}
        trainingName={selectedSurvey?.training_name}
        revealModal={!!selectedSurvey}
        onSubmitted={handleSurveySubmitted}
      />
    </>
  );
};

const SatisfactionSurveyBannerWithProvider = (props) => {
  return (
    <ApolloProvider client={apiClient}>
      <SatisfactionSurveyBanner {...props} />
    </ApolloProvider>
  );
};

export default withTranslation('js.components.satisfaction_meter')(
  SatisfactionSurveyBannerWithProvider,
);
